import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Pagination,
  Container,
  Row,
  Col,
  Spinner,
  Alert,
} from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaImage } from "react-icons/fa";

const API_URL = "https://radheyelectrical.com/api/projects.php"; // Update with your actual API path
const IMAGE_BASE_URL = "https://radheyelectrical.com/api/"; // For displaying images

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [formData, setFormData] = useState({
    id: "",
    title: "",
    client: "",
    location: "",
    date: "",
    desc: "",
    category: "",
    image: null,
  });

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      // Constructing URL with query parameters for GET
      const url = new URL(API_URL);
      url.searchParams.append("page", currentPage);
      url.searchParams.append("limit", itemsPerPage);

      const response = await fetch(url);
      const result = await response.json();

      if (result.status === "success") {
        setProjects(result.data);
        setTotalItems(result.total);
      } else {
        throw new Error(result.message || "Failed to fetch");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image") {
      setFormData({ ...formData, image: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleShow = (project = null) => {
    if (project) {
      setIsEdit(true);
      setFormData({
        id: project.id,
        title: project.title,
        client: project.client,
        location: project.location,
        date: project.date,
        desc: project.description,
        category: project.category,
        image: null,
      });
    } else {
      setIsEdit(false);
      setFormData({
        id: "",
        title: "",
        client: "",
        location: "",
        date: "",
        desc: "",
        category: "",
        image: null,
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true); // Start Spinner

    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === "image" && formData[key]) {
        data.append("image", formData[key]);
      } else if (key !== "image") {
        data.append(key, formData[key]);
      }
    });

    if (isEdit) {
      data.append("_method", "PUT");
    }

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        body: data,
      });

      const result = await response.json();

      if (result.status === "success") {
        setShowModal(false);
        fetchProjects();
      } else {
        alert(result.message);
      }
    } catch (err) {
      console.error("Submission error", err);
      alert("An error occurred while saving.");
    } finally {
      setIsSubmitting(false); // Stop Spinner regardless of success or failure
    }
  };
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      try {
        // Your PHP uses parse_str(file_get_contents("php://input")),
        // so we send as URL encoded body
        const response = await fetch(API_URL, {
          method: "DELETE",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({ id }).toString(),
        });

        const result = await response.json();
        if (result.status === "success") {
          fetchProjects();
        } else {
          alert(result.message);
        }
      } catch (err) {
        console.error("Delete error", err);
      }
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <Container className="mt-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h2>Project Gallery Admin</h2>
        </Col>
        <Col className="text-end">
          <Button variant="primary" onClick={() => handleShow()}>
            <FaPlus className="me-2" /> New Project
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table striped bordered hover responsive>
        <thead className="table-dark">
          <tr>
            <th>Preview</th>
            <th>Title</th>
            <th>Client</th>
            <th>Category</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="5" className="text-center">
                <Spinner animation="border" />
              </td>
            </tr>
          ) : (
            projects.map((proj) => (
              <tr key={proj.id}>
                <td>
                  {proj.image ? (
                    <img
                      src={`${IMAGE_BASE_URL}${proj.image}`}
                      alt="thumb"
                      style={{
                        width: "50px",
                        height: "50px",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <FaImage size={30} color="#ccc" />
                  )}
                </td>
                <td>{proj.title}</td>
                <td>{proj.client}</td>
                <td>{proj.category}</td>
                <td>
                  <Button
                    variant="outline-info"
                    size="sm"
                    className="me-2"
                    onClick={() => handleShow(proj)}
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDelete(proj.id)}
                  >
                    <FaTrash />
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <Pagination className="justify-content-center">
        {[...Array(totalPages)].map((_, i) => (
          <Pagination.Item
            key={i + 1}
            active={i + 1 === currentPage}
            onClick={() => setCurrentPage(i + 1)}
          >
            {i + 1}
          </Pagination.Item>
        ))}
      </Pagination>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? "Edit Project" : "Add Project"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Form.Control
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Client</Form.Label>
                  <Form.Control
                    name="client"
                    value={formData.client}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="desc"
                value={formData.desc}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Upload Image</Form.Label>
              <Form.Control
                type="file"
                name="image"
                onChange={handleChange}
                accept="image/*"
              />
              {isEdit && (
                <Form.Text className="text-muted">
                  Upload new to replace existing image.
                </Form.Text>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button
              variant="secondary"
              onClick={() => setShowModal(false)}
              disabled={isSubmitting}
            >
              Close
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting}
              style={{ minWidth: "120px" }}
            >
              {isSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Saving...
                </>
              ) : isEdit ? (
                "Update Project"
              ) : (
                "Save Project"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default AdminProjects;
